<template>
  <v-container fluid :loading="loading">
    <v-progress-linear
      :active="downloading"
      :indeterminate="downloading"
      color="red"
    />
    <v-row>
      <div style="margin: 0 0 0 auto">
        <v-btn @click="downloadGenderReport">
          性・年齢別データ ダウンロード
        </v-btn>
      </div>
    </v-row>
    <v-row>
      <v-col cols="6">
        <div style="width:100%" class="male-chart-wrapper" :key="maleKey">
          <apex-chart
            type="line"
            height="450"
            :options="charts.male.options"
            :series="charts.male.series"
          />
        </div>
      </v-col>
      <v-col cols="6">
        <div style="width:100%" class="female-chart-wrapper" :key="femaleKey">
          <apex-chart
            type="line"
            height="450"
            :options="charts.female.options"
            :series="charts.female.series"
          />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { Pick } from '@/protocol'
import { functions } from '@/config/firebase'
import apexChart from 'vue-apexcharts'
import { mapActions } from 'vuex'
import { reportFunctionsMixin, downloadFunctionsMixin } from '@/plugins/mixins'
import { getReportDetailsWithinByUnixtime } from '@/firebaseAccess/firestoreAccess'
import { fmt } from '@/plugins/datefns'
import { addDays, differenceInDays, endOfDay } from 'date-fns/esm'

const chartIds = {
  male: 'mc',
  female: 'fc'
}
const statsModel = (pass, look, interest, focus, touch) => {
  return {
    pass,
    look,
    interest,
    focus,
    touch
  }
}
const baseYAxis = numMax => {
  return {
    type: 'numeric',
    title: {
      text: '人数'
    },
    forceNiceScale: true,
    min: 0,
    max: numMax,
    labels: {
      formatter: value => {
        return Math.abs(Math.round(value)) + '人'
      }
    }
  }
}
const baseOptions = id => {
  return {
    chart: {
      id: id,
      // group: 'genderChart',
      type: 'line'
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'straight'
    },
    xaxis: {
      type: 'category',
      categories: [
        '0-9',
        '10-19',
        '20-29',
        '30-39',
        '40-49',
        '50-59',
        '60-69',
        '70-79',
        '80+'
      ],
      title: {
        text: '年齢'
      }
    }
  }
}

export default {
  name: 'GenderChart',
  props: {
    filters: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      required: false
    }
  },
  components: {
    apexChart
  },
  mixins: [reportFunctionsMixin, downloadFunctionsMixin],
  data() {
    return {
      charts: {
        male: {
          series: [],
          options: baseOptions(chartIds.male)
        },
        female: {
          series: [],
          options: baseOptions(chartIds.female)
        }
      },
      maleKey: 0,
      femaleKey: 0,
      downloading: false
    }
  },
  methods: {
    ...mapActions({
      showSnack: 'snackbar/showSnack'
    }),
    async updateChart(devices, startDate, endDate) {
      // Convert Date data into unixtime second(not milli second).
      const startDateSeconds = Math.floor(startDate.getTime() / 1000)
      const endDateSeconds = Math.floor(endDate.getTime() / 1000)

      const reports = []
      for (const deviceId of devices) {
        const reportRefs = await getReportDetailsWithinByUnixtime(
          deviceId,
          Pick.Report.ReportInfo.Type.TYPE_GENDER,
          startDateSeconds,
          endDateSeconds
        )

        for (const doc of reportRefs.docs) {
          reports.push(doc.data())
        }
      }

      const baseGenerationCount = () => [0, 0, 0, 0, 0, 0, 0, 0, 0]
      const baseGenderCount = () => [
        [...baseGenerationCount()],
        [...baseGenerationCount()],
        [...baseGenerationCount()],
        [...baseGenerationCount()],
        [...baseGenerationCount()]
      ]
      const genderReport = {
        female: statsModel(...baseGenderCount()),
        male: statsModel(...baseGenderCount())
      }
      for (const report of reports) {
        const genders = report.data
        for (const gender in genders) {
          for (const key in genders[gender]) {
            for (let i = 0; i < genders[gender][key].length; i++) {
              // Ignore 0-9 age because the minimum age to detect is 18-ish.
              // So age 0 means that age is not specified.
              if (i === 0) {
                continue
              }

              genderReport[gender][key][i] += genders[gender][key][i]
            }
          }
        }
      }

      // Give 5 as the minimum value of max value
      let maxNum = 5
      // Find max number of people which stats event have in male and female
      for (let gender in genderReport) {
        for (let key in genderReport[gender]) {
          for (let num of genderReport[gender][key]) {
            if (num > maxNum) {
              maxNum = num
            }
          }
        }
      }

      const eventCategories = this.getEventCategories()
      // Collect colors which each event has.
      const colors = eventCategories.map(e => e.color)

      for (const gender in genderReport) {
        const series = []
        // Collect series data.
        for (const event in genderReport[gender]) {
          const eventName = eventCategories.find(k => k.value === event).text
          series.push({
            name: eventName,
            data: genderReport[gender][event]
          })
        }

        const chart = this.charts[gender]
        const chartId = chartIds[gender]

        // Create common chart option.
        const chartOptions = {
          ...baseOptions(chartId),
          ...{
            color: colors,
            title: {
              text: ''
            },
            yaxis: baseYAxis(maxNum)
          }
        }

        // Note: Increment key to reload apex chart in order to update yaxis max.
        // This is not good way but couldn't find other ways.
        this.maleKey += 1
        this.femaleKey += 1

        chart.series = series
        chart.options = chartOptions
      }
    },
    async downloadGenderReport() {
      this.downloading = true

      const startDate = this.filters.date.start
      const endDate = this.filters.date.end
      const diffDays = differenceInDays(endDate, startDate) + 1

      let timeline = []
      // Populate start and end date by day.
      for (let i = 0; i < diffDays; i++) {
        const start = addDays(startDate, i)
        const time = {
          start: start.toString(),
          end: endOfDay(start).toString()
        }
        timeline.push(time)
      }

      const files = []
      // const anchors = []
      for (const deviceId of this.filters.devices) {
        for (const time of timeline) {
          const filename = `G_${deviceId}_${fmt(
            new Date(time.start),
            'yyyy-MM-dd'
          )}.json`

          try {
            const task = functions.httpsCallable('report-DownloadGenderReport')
            // Create report file and get download url
            const result = await task({
              deviceId: deviceId,
              startDate: time.start,
              endDate: time.end
            })

            if (result.data.success) {
              files.push({ name: filename, url: result.data.url })
            }
          } catch (error) {
            this.showSnack({
              message: `${filename}のダウンロードに失敗しました`,
              color: 'error'
            })
            console.log(error)
          }
        }
      }

      if (files.length > 0) {
        const folderName = `性・年齢別データ_${new Date()}`
        const zipBlob = await this.zipDownloadedFiles(
          folderName,
          files.map(f => f.url),
          10
        )

        if (zipBlob !== undefined && zipBlob !== null) {
          const urlObj = URL.createObjectURL(zipBlob)
          const anchor = this.downloadFile(folderName, urlObj)
          const message = `データをダウンロードしました`
          this.showSnack({ message })

          // Tidy up URL object to release memory.
          URL.revokeObjectURL(urlObj)
          // Tidy up anchor tags. sometime fail to download if removing anchor tag immediately, so delete them here.console.log(downloadUrl)
          anchor.remove()
        }
      }

      this.downloading = false
    }
  },
  watch: {
    filters() {
      if (
        this.filters.devices.length > 0 &&
        this.filters.date.start !== undefined &&
        this.filters.date.end !== undefined
      ) {
        this.updateChart(
          this.filters.devices,
          new Date(this.filters.date.start),
          new Date(this.filters.date.end)
        )
      } else {
        // do something
      }
    }
  }
}
</script>

<style scoped>
ul {
  list-style: none;
}

.male-chart-wrapper {
  position: relative;
}
.male-chart-wrapper::before {
  position: absolute;
  content: '男性';
  text-align: center;
  vertical-align: middle;
  font-size: 14px;
  font-weight: bold;
  top: 0;
  left: 0;
}

.female-chart-wrapper {
  position: relative;
}
.female-chart-wrapper::before {
  position: absolute;
  content: '女性';
  text-align: center;
  vertical-align: middle;
  font-size: 14px;
  font-weight: bold;
  top: 0;
  left: 0;
}
</style>
